import { useEffect, useState, useRef } from 'react';
import { isEmpty } from 'lodash';

import { useEnvStore } from '../../store/models';
import { SignInSignUpState } from '../../utils/constants';
import useOTPError from './useOTPError';

const TIME_REMAINING = 180;
const NEW_OTP_SENT_SUCCESS_MESSAGE = 'A new verification code has been sent to your email.';

/**
 * Custom hook to manage OTP verification state and logic
 * @param {boolean} open - Whether the OTP dialog is open
 * @param {Function} onVerify - Callback function to verify OTP
 * @param {Function} onResendOTP - Callback function to resend OTP
 * @returns {Object} State and handlers for OTP verification
 */
export const useVerifyOTP = (open, onVerify, onResendOTP) => {
  const envStore = useEnvStore();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [input, setInput] = useState({ otp: '' });
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [timeRemaining, setTimeRemaining] = useState(TIME_REMAINING);
  const [successMessage, setSuccessMessage] = useState('');
  const inputRefs = useRef([]);

  const { error, clearError, isLimitExceeded } = useOTPError();

  const resetOtpFields = () => {
    setOtpDigits(['', '', '', '', '', '']);
    setInput({ otp: '' });
  };

  const focusFirstInput = () => {
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 100);
  };

  const resetDialog = () => {
    setTimeRemaining(TIME_REMAINING);
    setIsVerifying(false);
    setIsResending(false);
    resetOtpFields();
    clearError();
    setSuccessMessage('');
  };

  const handleResendingState = () => {
    setIsResending(true);
  };

  const handleResendCompleteState = () => {
    setIsResending(false);
    setTimeRemaining(TIME_REMAINING);
    resetOtpFields();
    focusFirstInput();
    setSuccessMessage(NEW_OTP_SENT_SUCCESS_MESSAGE);
  };

  const handleVerifyingState = () => {
    setIsVerifying(true);
  };

  const handleVerifyCompleteState = () => {
    setIsVerifying(false);
  };

  const updateLoading = () => {
    const currentStatus = envStore.signInSignUpState.status;

    switch (currentStatus) {
      case SignInSignUpState.OTP_RESENDING:
        handleResendingState();
        break;
      case SignInSignUpState.OTP_RESEND:
        handleResendCompleteState();
        break;
      case SignInSignUpState.OTP_CONFIRMING:
        handleVerifyingState();
        break;
      case SignInSignUpState.OTP_CONFIRM_SUCCESS:
        handleVerifyCompleteState();
        break;
      default:
        break;
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    // Clear error and success message when user starts typing
    if (!isEmpty(error)) {
      clearError();
    }
    if (successMessage) {
      setSuccessMessage('');
    }

    const newOtpDigits = [...otpDigits];
    newOtpDigits[index] = value.slice(-1);
    setOtpDigits(newOtpDigits);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtpDigits = [...otpDigits];

    for (let i = 0; i < pastedData.length; i++) {
      newOtpDigits[i] = pastedData[i];
    }

    setOtpDigits(newOtpDigits);

    const nextEmptyIndex = pastedData.length < 6 ? pastedData.length : 5;
    inputRefs.current[nextEmptyIndex]?.focus();
  };

  const handleVerify = () => {
    setIsVerifying(true);
    clearError();
    onVerify(input.otp);
  };

  const handleResend = () => {
    setIsResending(true);
    clearError();
    resetOtpFields();
    setSuccessMessage('');
    focusFirstInput();
    onResendOTP();
  };

  // Update loading states based on sign in/sign up state
  useEffect(() => {
    updateLoading();
  }, [envStore.signInSignUpState.status]);

  // Handle confirm sign in error
  useEffect(() => {
    if (envStore.errorStore.authError.confirmSignIn) {
      setIsVerifying(false);
    }
  }, [envStore.errorStore.authError.confirmSignIn]);

  // Handle resend OTP error
  useEffect(() => {
    if (envStore.errorStore.authError.resendForOTP) {
      setIsResending(false);
    }
  }, [envStore.errorStore.authError.resendForOTP]);

  // Update input when OTP digits change
  useEffect(() => {
    const otp = otpDigits.join('');
    setInput({ otp });
  }, [otpDigits]);

  // Handle dialog open/close and countdown timer
  useEffect(() => {
    if (!open) return;

    // Focus the first input field after dialog opens
    focusFirstInput();

    // Set up countdown timer
    const countdownTimer = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownTimer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Cleanup when dialog closes
    return () => {
      clearInterval(countdownTimer);
      resetDialog();
    };
  }, [open]);

  return {
    isVerifying,
    isResending,
    input,
    otpDigits,
    timeRemaining,
    successMessage,
    inputRefs,
    error,
    isLimitExceeded,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerify,
    handleResend,
  };
};

export default useVerifyOTP;
